import { useEffect, useMemo, useState } from "react"

import { Button, Col, Container, Modal, ModalBody, ModalTitle, Row, Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import { BehaviorSubject } from "rxjs"
import { finalize, switchMap } from "rxjs/operators"

import FormatError from "../../../formatters/FormatError"
import TablePagination from "../../TablePagination"
import { PoolStatisticsTestDetail, PoolStatisticsTestsGridData, PoolStatisticsTestsGridOptions } from "../../../api/Randoms/RandomsModel/PoolStatisticsDetails"
import FormatDate from "../../../formatters/FormatDate"
import { PoolStatisticsTestDetails, PoolStatisticsTestDetailsExport } from "../../../api/Randoms/PoolStatisticsDetailsGet"
import FormatTimestampFileName from "../../../formatters/FormatTimestampFileName"
import FileSaver from "file-saver"
import ModalHeader from "react-bootstrap/esm/ModalHeader"
import { DraggableModalDialog } from "../../Randomization/GovPools/RandomSchedulingExclusionsModal"
import ActionButton from "../../ActionButton"

const headerColumns = ["First Name", "Last Name", "Primary ID", "Company", "Service", "Status", "Reason", "Result", "Regulated", "ReferenceID", "SampleID", "Event Date", "Report Date", "Pool Name", "Period"]
const PageSize = 5

interface PoolStatisticsTestsGridParams {
  type: string
  poolID: number
  accountID: number
  onClose: () => void
}

function PoolStatisticsTestsGrid(props: PoolStatisticsTestsGridParams) {
  const { type, poolID, accountID, onClose } = props
  const [gridData, setGridData] = useState<PoolStatisticsTestsGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const page$ = useMemo(
    () =>
      new BehaviorSubject<PoolStatisticsTestsGridOptions>({
        type,
        poolID,
        accountID,
        pageNumber: 1,
        pageSize: PageSize,
      }),
    []
  )

  const onCancel = () => {
    onClose()
  }

  useEffect(() => {
    setLoading(true)
    const subs = page$
      .pipe(
        switchMap((params) =>
          PoolStatisticsTestDetails(params).pipe(
            finalize(() => {
              setLoading(false);
            })
          )
        )
      )
      .subscribe(
        (data) => {
          setGridData(data)
          setLoading(false)
        },
        (error) => toast.error(FormatError(error))
      );
    return () => subs.unsubscribe()
  }, [page$])

  const handleChangePage = (page: number) => {
    setLoading(true)
    page$.next({ ...page$.value, pageNumber: page })
  }

  const handleExport = () => {
    setExporting(true)
    const params: PoolStatisticsTestsGridOptions = {
      poolID,
      type,
      accountID,
      pageNumber: 1,
      pageSize: PageSize
    }
    PoolStatisticsTestDetailsExport(params)
      .pipe(finalize(() => setExporting(false)))
      .subscribe(
        (data) => exportFile(data, "PoolStatisticsTestDetails"),
        (error) => toast.error(FormatError(error))
      )
  }

  const exportFile = (csv: string, fileName: string) => {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName(fileName, "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  const dialogTitle = (type: string): string => {
      const completedTitle = "TOTAL TESTS COMPLETED"
      const pendingTitle = "TOTAL TESTS PENDING COLLECTION"
      if(type == "Completed"){
        return completedTitle
      }
      if (type == "Pending") {
        return pendingTitle
      }
      return ""
    }

  const modalTitle = dialogTitle(type)

  return (
    <>
      <Modal
        dialogAs={DraggableModalDialog}
        backdrop="static"
        show
        size="xl"
        dialogClassName="dark"
        onHide={onClose}
      >
        <ModalHeader closeButton>
          <ModalTitle>{modalTitle}</ModalTitle>
        </ModalHeader>
        <ModalBody style={{ overflowX: "auto" }}>
          <Container fluid className="grid-container event-grid">
            <Row>
              <Col>
                <Table bordered hover responsive>
                  <thead className="thead-light">
                    <tr>
                      {headerColumns.map((el) => (
                        <th key={`dh_${el}`}>{el}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-light">
                    {loading && (
                      <tr>
                        <td colSpan={headerColumns.length}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      (gridData.Records.length > 0 ? (
                        gridData.Records.map(
                          (row: PoolStatisticsTestDetail) => (
                            <tr key={`${row.TestDetailID}`}>
                              <td className="service-name">{row.FirstName}</td>
                              <td className="service-name">{row.LastName}</td>
                              <td className="service-name">{row.PrimaryID}</td>
                              <td className="service-name">{row.CompanyName}</td>
                              <td className="service-name">{row.Service}</td>
                              <td className="test-reason">{row.Status}</td>
                              <td className="test-reason">{row.Reason}</td>
                              <td className="test-result">{row.Result}</td>
                              <td>{row.Regulated}</td>
                              <td>{row.FormFoxTestAN > 0 ? row.FormFoxTestAN : ''}</td>
                              <td>{row.SampleID}</td>
                              <td>{FormatDate(row.EventDate)}</td>
                              <td>{FormatDate(row.ReportDate)}</td>
                              <td className="service-name">{row.PoolName}</td>
                              <td className="service-name">{row.PeriodName}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan={headerColumns.length}>NO DATA FOUND</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
                {gridData.Records.length > 0 && (
                  <>
                    <span className="total-records">
                      Total records: {gridData.Total}
                    </span>
                    <TablePagination
                      pageNumber={page$.value.pageNumber}
                      pages={gridData.Pages}
                      onChange={handleChangePage}
                    />
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </ModalBody>
        <Modal.Footer>
          <Button variant="primary" onClick={onCancel}>
            CANCEL
          </Button>
          <ActionButton
            variant="secondary"
            onClick={handleExport}
            disabled={!!loading || !!exporting || gridData.Records.length == 0}
            spin={exporting}
          >
            EXPORT CSV
          </ActionButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PoolStatisticsTestsGrid
